import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { KeyRound, Trash2, ShieldHalf, SlidersHorizontal, ArrowUp, ArrowDown, ArrowUpDown, Crown, Search } from "lucide-react";
import AccessDrawer from "./access/AccessDrawer";
import PresetManager from "./access/PresetManager";
import AccessRequestsPanel from "./access/AccessRequestsPanel";
import UnassignedLevels from "./access/UnassignedLevels";
import { fetchNavTree, groupTree, ACCESS_ROLE_LABELS, ACCESS_ROLE_ORDER, fetchAccessUsers, patchAccessUser } from "../lib/access";
import { useCapabilityCatalog } from "./access/AbilitiesEditor";

function SortTh({ label, sortKey, activeKey, dir, onSort }) {
  const active = activeKey === sortKey;
  return (
    <th className="select-none">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`admin-users-th-${sortKey}`}
        className={`inline-flex items-center gap-1 hover:text-white transition-colors ${active ? "text-white" : ""}`}
        title={`Sort by ${label}`}
      >
        {label}
        {active
          ? (dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
          : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </button>
    </th>
  );
}


export default function AdminUsers() {  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [resetting, setResetting] = useState(null); // user object being reset
  const [newPassword, setNewPassword] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [tab, setTab] = useState("users"); // users | presets
  const [groups, setGroups] = useState([]);
  const [accessUser, setAccessUser] = useState(null); // user whose access drawer is open
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [q, setQ] = useState("");
  const [accessF, setAccessF] = useState("all");
  const caps = useCapabilityCatalog();
  const onHeaderSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortKey(null); setSortDir("asc");
  };
  const accessKey = (u) => (u.super_admin ? "super" : u.role === "admin" ? "admin" : u.access_role || "unrestricted");
  const filteredUsers = useMemo(() => {
    const n = q.trim().toLowerCase();
    return users.filter((u) => (accessF === "all" || accessKey(u) === accessF || (accessF === "admin" && accessKey(u) === "super"))
      && (!n || [u.name, u.email, u.title].some((v) => String(v || "").toLowerCase().includes(n))));
  }, [users, q, accessF]);
  const meSuper = !!users.find((u) => u.id === user.id)?.super_admin;
  const toggleSuper = async (u) => {
    if (!window.confirm(`${u.super_admin ? "Remove" : "Grant"} super admin for ${u.name}?`)) return;
    try { await patchAccessUser(u.id, { super_admin: !u.super_admin }); flash(`Super admin ${u.super_admin ? "removed" : "granted"} — ${u.name}`); load(); }
    catch (e) { setError(formatApiError(e?.response?.data?.detail) || "Could not change super admin"); }
  };
  const sortedUsers = useMemo(() => {
    const users = filteredUsers;
    if (!sortKey) return users;
    const val = (u) => {
      switch (sortKey) {
        case "name":  return (u.name || "").toLowerCase();
        case "email": return (u.email || "").toLowerCase();
        case "title": return (u.title || "").toLowerCase();
        case "role":  return ["super", "admin", "manager", "operator", "finance", "supplier", "view_only", "unrestricted"].indexOf(accessKey(u));
        default:      return 0;
      }
    };
    const dir = sortDir === "asc" ? 1 : -1;
    return [...users].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [filteredUsers, sortKey, sortDir]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, acc] = await Promise.all([api.get("/users", { params: { include_hidden: true } }), fetchAccessUsers().catch(() => [])]);
      const byId = Object.fromEntries((acc || []).map((a) => [a.id, a]));
      setUsers(data.map((u) => ({ ...u, super_admin: !!byId[u.id]?.super_admin, capabilities: byId[u.id]?.capabilities || [], capability_overrides: byId[u.id]?.capability_overrides || [] })));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { fetchNavTree().then((p) => setGroups(groupTree(p))).catch(() => {}); }, []);

  const flash = (msg) => { setInfo(msg); setTimeout(() => setInfo(""), 3000); };

  const updateRole = async (u, access_role) => {
    setError("");
    const label = ACCESS_ROLE_LABELS[access_role];
    if ((access_role === "admin" || u.role === "admin") && !window.confirm(access_role === "admin"
      ? `Make ${u.name} an Admin? They get every area plus Config and user management.`
      : `Move ${u.name} off Admin to ${label}? They lose Config and user management.`)) return;
    try {
      await patchAccessUser(u.id, { access_role, keep_overrides: true });
      flash(`${u.name} is now ${label}`);
      load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Failed to update role");
    }
  };

  const updateTitle = async (id, title) => {
    setError("");
    try {
      const { data } = await api.patch(`/users/${id}`, { title });
      setUsers((us) => us.map((u) => (u.id === id ? data : u)));
      flash("Title updated");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Failed to update title");
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) { setError("Password must be at least 6 characters."); return; }
    try {
      await api.post(`/users/${resetting.id}/reset-password`, { new_password: newPassword });
      flash(`Password reset for ${resetting.name}`);
      setResetting(null);
      setNewPassword("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Reset failed");
    }
  };

  const confirmDelete = async () => {
    setError("");
    try {
      await api.delete(`/users/${deleting.id}`);
      setUsers((us) => us.filter((u) => u.id !== deleting.id));
      flash(`Deleted ${deleting.name}`);
      setDeleting(null);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  if (user?.role !== "admin") {
    return <Layout><div className="text-slate-400">Admin access required.</div></Layout>;
  }

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="font-display text-4xl font-extrabold tracking-tight">User Management</h1>
        <p className="text-slate-400 mt-1">Manage roles, page access, reset passwords, and remove accounts.</p>
      </div>

      <div className="flex gap-2 mb-5" data-testid="users-tabs">
        <button
          onClick={() => setTab("users")}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
            tab === "users" ? "bg-orange-500/15 border-orange-400/50 text-orange-200" : "border-white/10 text-slate-300 hover:bg-white/5"
          }`}
          data-testid="tab-users"
        >
          <ShieldHalf className="w-4 h-4" /> Users
        </button>
        <button
          onClick={() => setTab("presets")}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
            tab === "presets" ? "bg-orange-500/15 border-orange-400/50 text-orange-200" : "border-white/10 text-slate-300 hover:bg-white/5"
          }`}
          data-testid="tab-presets"
        >
          <SlidersHorizontal className="w-4 h-4" /> Manage Presets
        </button>
      </div>

      {error && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-4" data-testid="admin-error">{error}</div>}
      {info && <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 mb-4" data-testid="admin-info">{info}</div>}

      {tab === "presets" ? (
        <div className="card-dark p-5"><PresetManager groups={groups} /></div>
      ) : (
      <div>
      <UnassignedLevels onApplied={load} />
      <AccessRequestsPanel onResolved={load} />
      <div className="card-dark p-5">
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="users-toolbar">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, title…" data-testid="users-search"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-sm text-white outline-none focus:border-orange-400/60" />
          </div>
          {[["all", "All"], ["super", "Super admin"], ["admin", "Admin"], ["manager", "Management"], ["operator", "Operator"], ["finance", "Finance"], ["supplier", "Supplier"], ["view_only", "View only"], ["unrestricted", "Unrestricted"]].map(([k, l]) => {
            const n = k === "all" ? users.length : users.filter((u) => accessKey(u) === k || (k === "admin" && accessKey(u) === "super")).length;
            return (
              <button key={k} type="button" onClick={() => setAccessF(k)} data-testid={`users-filter-${k}`}
                className={`px-2.5 py-1.5 rounded-full text-[11px] font-medium border transition-colors ${accessF === k ? "bg-orange-500/15 border-orange-400/50 text-orange-200" : "border-white/10 text-slate-400 hover:text-white"}`}>
                {l} <span className="opacity-60">{n}</span>
              </button>
            );
          })}
        </div>
        {loading ? <div className="p-6 text-slate-400 text-sm">Loading...</div> : (
          <div className="overflow-hidden rounded-xl border border-white/5">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="tbl-header">
                <tr>
                  <SortTh label="Name"  sortKey="name"  activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                  <SortTh label="Email" sortKey="email" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                  <SortTh label="Title" sortKey="title" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                  <SortTh label="Role" sortKey="role" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                  <th>Access</th><th>Special actions</th><th>Actions</th>
                </tr>
              </thead>
              <tbody className="tbl-body">
                {sortedUsers.map((u) => {
                  const isSelf = u.id === user.id;
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td className="font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {u.name}
                          {u.super_admin && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest bg-amber-500/15 text-amber-300 border border-amber-500/40" data-testid={`super-badge-${u.id}`}><Crown className="w-2.5 h-2.5" /> Super</span>}
                        </span>
                      </td>
                      <td>{u.email}</td>
                      <td>
                        <TitleCell user={u} onSave={(t) => updateTitle(u.id, t)} />
                      </td>
                      <td>
                        <select value={u.role === "admin" ? "admin" : u.access_role || ""} onChange={(e) => e.target.value && updateRole(u, e.target.value)}
                          className="border border-slate-300 rounded px-2 py-1 text-sm bg-white text-slate-900 w-36" data-testid={`role-select-${u.id}`}
                          disabled={isSelf || u.super_admin} title={u.super_admin ? "Super admins are always Admin" : isSelf ? "You can't change your own role" : ""}>
                          {!u.access_role && u.role !== "admin" && <option value="">Unrestricted</option>}
                          {ACCESS_ROLE_ORDER.map((r) => <option key={r} value={r}>{ACCESS_ROLE_LABELS[r]}</option>)}
                        </select>
                      </td>
                      <td>
                        <AccessCell u={u} onEdit={() => setAccessUser(u)} />
                      </td>
                      <td data-testid={`abilities-cell-${u.id}`}>
                        <span className="text-[11px] font-mono text-slate-300" title={caps.map((c) => `${(u.capabilities || []).includes(c.key) ? "✓" : "✗"} ${c.label}`).join("\n")}>
                          {(u.capabilities || []).length}/{caps.length || 6}
                          {(u.capability_overrides || []).length > 0 && <span className="ml-1 text-fuchsia-300">· custom</span>}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          {meSuper && (
                            <button onClick={() => toggleSuper(u)} disabled={isSelf}
                              title={isSelf ? "You can't remove your own super admin" : u.super_admin ? "Remove super admin" : "Make super admin"}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border disabled:opacity-40 ${u.super_admin ? "border-amber-500/40 text-amber-300 bg-amber-500/10" : "border-white/10 text-slate-400 hover:text-white"}`}
                              data-testid={`super-toggle-${u.id}`}>
                              <Crown className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => { setResetting(u); setNewPassword(""); setError(""); }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-500"
                            data-testid={`reset-password-${u.id}`}
                          >
                            <KeyRound className="w-3.5 h-3.5" /> Reset
                          </button>
                          <button
                            onClick={() => { setDeleting(u); setError(""); }}
                            disabled={isSelf}
                            title={isSelf ? "You cannot delete your own account" : "Delete user"}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-red-600 text-white hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
      )}

      {accessUser && (
        <AccessDrawer
          user={accessUser}
          groups={groups}
          onClose={() => setAccessUser(null)}
          onSaved={load}
        />
      )}

      {resetting && (
        <Modal title={`Reset password for ${resetting.name}`} onClose={() => setResetting(null)} testId="reset-password-modal">
          <form onSubmit={submitReset} className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-300">New password</label>
              <input
                type="password" required minLength={6} autoFocus
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                className="input-dark mt-1.5"
                data-testid="reset-password-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">Minimum 6 characters.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setResetting(null)} className="px-3 py-2 rounded-md text-sm border border-white/10 hover:bg-white/5">Cancel</button>
              <button type="submit" className="btn-coral" data-testid="reset-password-confirm">Reset password</button>
            </div>
          </form>
        </Modal>
      )}

      {deleting && (
        <Modal title={`Delete ${deleting.name}?`} onClose={() => setDeleting(null)} testId="delete-user-modal">
          <p className="text-sm text-slate-300">This will permanently remove <b>{deleting.email}</b> from the system. Items created or assigned to this user remain but the user reference will be lost.</p>
          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={() => setDeleting(null)} className="px-3 py-2 rounded-md text-sm border border-white/10 hover:bg-white/5">Cancel</button>
            <button onClick={confirmDelete} className="px-4 py-2 rounded-md text-sm font-semibold bg-red-600 text-white hover:bg-red-500" data-testid="delete-user-confirm">Yes, delete</button>
          </div>
        </Modal>
      )}
    </Layout>
  );
}

function AccessCell({ u, onEdit }) {
  const isAuthAdmin = u.role === "admin";
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-slate-400" data-testid={`access-badge-${u.id}`}>
        {isAuthAdmin ? "Everything" : u.access_role ? `${ACCESS_ROLE_LABELS[u.access_role] || u.access_role} preset` : "Not restricted"}
      </span>
      {u.is_custom && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest bg-fuchsia-500/10 text-fuchsia-300 border border-fuchsia-500/30" data-testid={`access-custom-${u.id}`}>
          Custom
        </span>
      )}
      <button onClick={onEdit} title={isAuthAdmin ? "View access (admins have everything)" : "Edit what this person can see and do"}
        className="text-xs px-2 py-1 rounded-md border border-white/10 text-slate-300 hover:bg-white/5" data-testid={`access-edit-${u.id}`}>
        {isAuthAdmin ? "View" : "Edit access"}
      </button>
    </div>
  );
}

function Modal({ title, children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" data-testid={testId} onClick={onClose}>
      <div className="card-dark w-full max-w-md p-6 fade-up" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-lg font-bold mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function TitleCell({ user, onSave }) {
  const [value, setValue] = useState(user.title || "");
  const [dirty, setDirty] = useState(false);

  const commit = async () => {
    const trimmed = value.trim();
    if (trimmed === (user.title || "")) { setDirty(false); return; }
    await onSave(trimmed);
    setDirty(false);
  };

  return (
    <input
      type="text"
      value={value}
      placeholder="Add title..."
      onChange={(e) => { setValue(e.target.value); setDirty(true); }}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } if (e.key === "Escape") { setValue(user.title || ""); setDirty(false); e.target.blur(); } }}
      className={`border rounded px-2 py-1 text-sm bg-white text-slate-900 w-44 ${dirty ? "border-orange-400 ring-1 ring-orange-200" : "border-slate-300"}`}
      data-testid={`title-input-${user.id}`}
    />
  );
}
